import { MNSproCloudApi } from '..';
import { EffectivePermissionsResponse, Feature, UserSettings } from '../api/v2';

/**
 * Shape of the raw response. Older backends answered with a plain permission array,
 * so both shapes have to be accepted until every environment is updated.
 */
type EffectivePermissionsPayload = string[] | Partial<EffectivePermissionsResponse>;

export class MeService {
  static async getProfile(authToken: string) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.UsersApiFactory(config);

    const profile = await api.getProfile();
    return profile.data;
  }

  static async getMeActiveFeatures(
    authToken: string,
    organizationId: string,
  ): Promise<Feature[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.OrganizationsApiFactory(config);

    const rights = await api.getFeatures(organizationId);
    return rights.data;
  }

  /**
   * Returns the effective permissions of the current user in the given organization
   * together with the flags the backend provides for them: `canGiveAppConsent`
   * for a member of an Entra directory role that may grant the tenant wide admin
   * consent our apps require.
   */
  static async getEffectivePermissions(
    authToken: string,
    organizationId: string,
  ): Promise<EffectivePermissionsResponse> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.PermissionsApiFactory(config);

    const response = await api.getEffectivePermissionsOfMyself(organizationId);
    const payload = response.data as unknown as EffectivePermissionsPayload;

    if (Array.isArray(payload)) {
      return { permissions: payload, canGiveAppConsent: false };
    }

    return {
      permissions: payload.permissions ?? [],
      canGiveAppConsent: payload.canGiveAppConsent ?? false,
    };
  }

  static async updateUserSettings(
    authToken: string,
    organizationId: string,
    settings: UserSettings,
  ) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.UsersApiFactory(config);

    const settingsResult = await api.updateSettings(organizationId, settings);
    return settingsResult.data;
  }
}
